// PlanoTracer.js — canvas de trazado sobre un plano, con interacción de
// nivel CAD: zoom (rueda) + pan (arrastre botón derecho), línea de
// previsualización en vivo con cota mientras se dibuja, snapping a puntos
// existentes y a ángulos rectos, selección/borrado de lo ya dibujado, y el
// equipo movible por arrastre. No conoce Firebase ni el motor de cálculo —
// solo captura geometría y la devuelve por callbacks; el nombre/tipo/
// detalles de cada estancia o tramo se terminan de editar en las tablas de
// CalculadoraConductosView.js.
//
// Coordenadas: todo lo que se guarda (poligonoPx, trazadoPx, equipoPos) está
// en "espacio del plano" (world) — el mismo sistema de coordenadas fijo del
// plano de fondo tal como se renderiza (sin zoom/pan aplicado). El zoom/pan
// es solo una transformación de vista sobre ese espacio fijo, así que los
// proyectos ya guardados siguen siendo válidos sin migración.
import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { areaPoligonoM2, longitudTrazadoM, calcularMmPorPx, distanciaPx, puntoMasCercanoEnPolilinea } from '../../utils/planoGeo';

function centroide(puntos) {
  const n = puntos.length;
  const sum = puntos.reduce((s, p) => ({ x: s.x + p.x, y: s.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / n, y: sum.y / n };
}

function puntoEnPoligono(pt, poligono) {
  let dentro = false;
  for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i++) {
    const xi = poligono[i].x, yi = poligono[i].y, xj = poligono[j].x, yj = poligono[j].y;
    const interseca = (yi > pt.y) !== (yj > pt.y) && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi;
    if (interseca) dentro = !dentro;
  }
  return dentro;
}

function distanciaPuntoASegmento(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return distanciaPx(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return distanciaPx(p, { x: a.x + t * dx, y: a.y + t * dy });
}

function distanciaPuntoAPolilinea(p, puntos) {
  let min = Infinity;
  for (let i = 0; i < puntos.length - 1; i++) min = Math.min(min, distanciaPuntoASegmento(p, puntos[i], puntos[i + 1]));
  return min;
}

// Snap a un ángulo recto respecto al punto anterior — los tramos salen en
// escuadra sin necesitar pulso perfecto, igual que en un editor CAD real.
function snapOrtogonal(anterior, actual, gradosTol) {
  const dx = actual.x - anterior.x, dy = actual.y - anterior.y;
  if (dx === 0 && dy === 0) return actual;
  const ang = Math.abs((Math.atan2(dy, dx) * 180) / Math.PI);
  if (ang < gradosTol || ang > 180 - gradosTol) return { x: actual.x, y: anterior.y };
  if (Math.abs(ang - 90) < gradosTol) return { x: anterior.x, y: actual.y };
  return actual;
}

// Comprueba si un punto (world) cae dentro del rectángulo del equipo,
// teniendo en cuenta su rotación — se rota el punto al espacio local del
// equipo (rotación inversa) y se compara contra el rectángulo sin girar.
function puntoEnEquipoRotado(world, equipoPos, w, h) {
  if (!equipoPos) return false;
  const rad = -((equipoPos.rotacion || 0) * Math.PI) / 180;
  const dx = world.x - equipoPos.x, dy = world.y - equipoPos.y;
  const localX = dx * Math.cos(rad) - dy * Math.sin(rad);
  const localY = dx * Math.sin(rad) + dy * Math.cos(rad);
  return Math.abs(localX) < w / 2 && Math.abs(localY) < h / 2;
}

// Punto del borde del equipo (en world) donde sale un conducto en dirección
// (ux,uy) — intersección de esa dirección con el rectángulo del equipo,
// respetando su rotación. Sirve de ancla para la embocadura: si se dibujara
// desde el centro del equipo quedaría tapada por su propio rectángulo.
function bordeEquipoHaciaDireccion(equipoPos, ux, uy, w, h) {
  const rad = -((equipoPos.rotacion || 0) * Math.PI) / 180;
  const lux = ux * Math.cos(rad) - uy * Math.sin(rad);
  const luy = ux * Math.sin(rad) + uy * Math.cos(rad);
  const tx = lux !== 0 ? (w / 2) / Math.abs(lux) : Infinity;
  const ty = luy !== 0 ? (h / 2) / Math.abs(luy) : Infinity;
  const t = Math.min(tx, ty);
  const lx = lux * t, ly = luy * t;
  const rad2 = ((equipoPos.rotacion || 0) * Math.PI) / 180;
  return {
    x: equipoPos.x + (lx * Math.cos(rad2) - ly * Math.sin(rad2)),
    y: equipoPos.y + (lx * Math.sin(rad2) + ly * Math.cos(rad2)),
  };
}

// Conector/collarín de unión — cuadrado gris con borde oscuro y una cruz en
// diagonal, recuerda al clip metálico real que sujeta dos tramos de
// conducto (visto en fotos de instalaciones reales) en vez del simple
// anillo fino de antes. Tamaño fijo en pantalla, como el resto de
// anotaciones de texto — no escala con el zoom.
function dibujarConector(ctx, p, tamano = 15) {
  const s = tamano / 2;
  ctx.fillStyle = '#94a3b8';
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1.8;
  ctx.fillRect(p.x - s, p.y - s, s * 2, s * 2);
  ctx.strokeRect(p.x - s, p.y - s, s * 2, s * 2);
  ctx.beginPath();
  ctx.moveTo(p.x - s, p.y - s); ctx.lineTo(p.x + s, p.y + s);
  ctx.moveTo(p.x + s, p.y - s); ctx.lineTo(p.x - s, p.y + s);
  ctx.stroke();
}

// Accesorios en línea (compuerta/silenciador/filtro): círculo de color con
// una letra, tamaño fijo en pantalla como el resto de anotaciones. Terminal
// (rejilla/difusor, en el extremo de un tramo): igual pero con un cuadrado
// en vez de círculo, para distinguirlos a simple vista sin tener que leer
// la letra.
const ACCESORIO_ESTILO = {
  compuerta: { color: '#64748b', letra: 'C' },
  silenciador: { color: '#0ea5e9', letra: 'S' },
  filtro: { color: '#f97316', letra: 'F' },
};
const TERMINAL_ESTILO = {
  rejilla: { color: '#14b8a6', letra: 'R' },
  difusor: { color: '#ec4899', letra: 'D' },
};
function dibujarAccesorio(ctx, p, tipo, isSel, tamano = 16) {
  const est = ACCESORIO_ESTILO[tipo]; if (!est) return;
  const s = tamano / 2;
  ctx.fillStyle = est.color;
  ctx.strokeStyle = isSel ? '#facc15' : '#1f2937';
  ctx.lineWidth = isSel ? 2.5 : 1.5;
  ctx.beginPath(); ctx.arc(p.x, p.y, s, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  const prevAlign = ctx.textAlign, prevBaseline = ctx.textBaseline;
  ctx.fillStyle = '#fff'; ctx.font = 'bold 9px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(est.letra, p.x, p.y + 0.5);
  ctx.textAlign = prevAlign; ctx.textBaseline = prevBaseline;
}
function dibujarTerminal(ctx, p, tipo, isSel, tamano = 16) {
  const est = TERMINAL_ESTILO[tipo]; if (!est) return;
  const s = tamano / 2;
  ctx.fillStyle = est.color;
  ctx.strokeStyle = isSel ? '#facc15' : '#1f2937';
  ctx.lineWidth = isSel ? 2.5 : 1.5;
  ctx.fillRect(p.x - s, p.y - s, tamano, tamano);
  ctx.strokeRect(p.x - s, p.y - s, tamano, tamano);
  const prevAlign = ctx.textAlign, prevBaseline = ctx.textBaseline;
  ctx.fillStyle = '#fff'; ctx.font = 'bold 9px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(est.letra, p.x, p.y + 0.5);
  ctx.textAlign = prevAlign; ctx.textBaseline = prevBaseline;
}

// Igual que buscarSnapEnTrazados, pero además devuelve a qué tramo
// pertenece el punto enganchado — para colocar un accesorio hace falta
// saber en qué tramo se está insertando, no solo la coordenada.
function buscarTramoCercano(puntoWorld, tramos, zoom, tolPx) {
  const tolWorld = tolPx / zoom;
  let mejor = null, mejorDist = tolWorld, mejorTramoId = null;
  for (const t of tramos) {
    if (!t.trazadoPx || t.trazadoPx.length < 2) continue;
    const cercano = puntoMasCercanoEnPolilinea(puntoWorld, t.trazadoPx);
    if (cercano && cercano.distancia < mejorDist) { mejor = cercano.punto; mejorDist = cercano.distancia; mejorTramoId = t.id; }
  }
  return mejor ? { tramoId: mejorTramoId, punto: mejor } : null;
}

function candidatosSnap(estancias, tramos, equipoPos) {
  const pts = [];
  for (const e of estancias) if (e.poligonoPx) for (const p of e.poligonoPx) pts.push(p);
  for (const t of tramos) if (t.trazadoPx) for (const p of t.trazadoPx) pts.push(p);
  if (equipoPos) pts.push(equipoPos);
  return pts;
}

function buscarSnap(puntoWorld, candidatos, zoom, tolPx) {
  const tolWorld = tolPx / zoom;
  let mejor = null, mejorDist = tolWorld;
  for (const c of candidatos) {
    const d = distanciaPx(puntoWorld, c);
    if (d < mejorDist) { mejor = c; mejorDist = d; }
  }
  return mejor;
}

// Enganche a mitad del trazado de CUALQUIER tramo existente (no solo a sus
// vértices) — para poder arrancar una derivación en cualquier punto de un
// tronco largo, tal como se diseña una instalación real. Se usa solo para
// el primer punto de un tramo nuevo, y solo si el snap a puntos discretos
// (buscarSnap) no encontró nada — los vértices siguen teniendo prioridad.
function buscarSnapEnTrazados(puntoWorld, tramos, zoom, tolPx) {
  const tolWorld = tolPx / zoom;
  let mejor = null, mejorDist = tolWorld;
  for (const t of tramos) {
    if (!t.trazadoPx || t.trazadoPx.length < 2) continue;
    const cercano = puntoMasCercanoEnPolilinea(puntoWorld, t.trazadoPx);
    if (cercano && cercano.distancia < mejorDist) { mejor = cercano.punto; mejorDist = cercano.distancia; }
  }
  return mejor;
}

const MODOS = [
  { id: 'calibrar', label: 'Calibrar escala', color: '#2563eb' },
  { id: 'estancia', label: 'Nueva estancia', color: '#16a34a' },
  { id: 'tramo', label: 'Nuevo tramo', color: '#dc2626' },
  { id: 'equipo', label: 'Equipo', color: '#7c3aed' },
  { id: 'accesorio', label: 'Accesorio', color: '#64748b' },
];
const TIPOS_ACCESORIO = [
  { id: 'compuerta', label: 'Compuerta' },
  { id: 'silenciador', label: 'Silenciador' },
  { id: 'filtro', label: 'Filtro' },
];

// Tamaño del equipo a escala real (mm, convertidos a unidades "mundo" con
// mmPorPx, igual que ya se hace con el grosor de los tramos) en vez de un
// tamaño de pantalla fijo — antes EQUIPO_W/H eran constantes en unidades
// mundo aplicadas tal cual, así que su tamaño real dependía por completo de
// a qué escala estuviera calibrado cada plano concreto: con una calibración
// amplia (mmPorPx alto) el equipo podía acabar viéndose varios metros de
// ancho. anchoMm/altoMm se guardan en el propio equipoPos (editables desde
// el panel de selección); EQUIPO_ANCHO/ALTO_MM_DEFECTO son un tamaño de
// referencia razonable para una unidad interior tipo split/cassette vista
// en planta, no un dato de fabricante. Sin mmPorPx todavía (plano sin
// calibrar) no hay forma de convertir mm a unidades mundo, así que se cae
// al tamaño de pantalla fijo de siempre.
const EQUIPO_ANCHO_MM_DEFECTO = 900, EQUIPO_ALTO_MM_DEFECTO = 300;
const EQUIPO_W_FALLBACK = 60, EQUIPO_H_FALLBACK = 40;
function tamanoEquipoMundo(equipoPos, mmPorPx) {
  if (!mmPorPx) return { w: EQUIPO_W_FALLBACK, h: EQUIPO_H_FALLBACK };
  const anchoMm = equipoPos?.anchoMm || EQUIPO_ANCHO_MM_DEFECTO;
  const altoMm = equipoPos?.altoMm || EQUIPO_ALTO_MM_DEFECTO;
  return { w: anchoMm / mmPorPx, h: altoMm / mmPorPx };
}

// Pasos de cuadrícula "bonitos" en mm — se elige el más pequeño que, a la
// escala/zoom actuales, deje al menos ~30px entre líneas en pantalla, para
// que la rejilla nunca se vea ni saturada ni demasiado suelta.
const PASOS_GRID_MM = [50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000];
const GRID_OBJETIVO_PX = 30;

const SNAP_TOL_PX = 10;
const VERTICE_TOL_PX = 9;
const ORTHO_SNAP_DEG = 10;
const VIEW_W = 1000, VIEW_H = 650;

// `bgSnapshotUrl` (opcional): fuente alternativa a `file` para el fondo —
// una imagen ya renderizada (data URL) en vez de un PDF/JPG/PNG original a
// procesar. Se usa al reabrir un proyecto guardado: en vez de tener que
// volver a subir/descargar el archivo original (necesitaría Storage), se
// guarda un snapshot del propio canvas de fondo ya renderizado (ver
// getFondoSnapshot más abajo) directamente en el documento de Firestore, y
// al reabrir se carga esa imagen tal cual — más simple y sin depender de
// que el archivo original siga disponible en ningún sitio. Si llega `file`
// (una subida nueva desde el input), tiene prioridad sobre `bgSnapshotUrl`.
const PlanoTracer = forwardRef(function PlanoTracer({
  file, bgSnapshotUrl, mmPorPx, onCalibrar, estancias = [], tramos = [], onNuevaEstancia, onNuevoTramo,
  equipoPos = null, onColocarEquipo, onRotarEquipo, onTamanoEquipo, onEliminarEstancia, onEliminarTramo,
  onEditarPuntoTramo, onEditarPuntoEstancia, onNuevoAccesorio, onEliminarAccesorio, junctions = [],
}, ref) {
  const canvasRef = useRef(null);
  const bgRef = useRef(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [error, setError] = useState('');
  const [modo, setModo] = useState(null);
  const [tipoAccesorio, setTipoAccesorio] = useState('compuerta');
  const [puntos, setPuntos] = useState([]);
  const [calibPendiente, setCalibPendiente] = useState(null);
  const [distanciaInput, setDistanciaInput] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [mostrarGrid, setMostrarGrid] = useState(true);
  // Selección múltiple: array de {tipo:'estancia'|'tramo'|'equipo', id?}.
  // Con exactamente 1 tramo/estancia seleccionado se pueden editar sus
  // vértices arrastrándolos (ver buscarVerticeCercano/dragRef tipo 'vertice').
  const [seleccionados, setSeleccionados] = useState([]);
  const estaSeleccionado = (tipo, id) => seleccionados.some(s => s.tipo === tipo && (tipo === 'equipo' || s.id === id));

  const zoomRef = useRef(zoom); zoomRef.current = zoom;
  const panRef = useRef(pan); panRef.current = pan;
  const mouseWorldRef = useRef(null);
  const dragRef = useRef(null); // {tipo:'pan'|'equipo', startScreen, startPan?/startEquipoPos?, moved, livePos?}
  const justDraggedRef = useRef(false);

  const screenToWorld = useCallback((s) => ({
    x: (s.x - panRef.current.x) / zoomRef.current,
    y: (s.y - panRef.current.y) / zoomRef.current,
  }), []);
  const worldToScreen = useCallback((w) => ({
    x: panRef.current.x + w.x * zoomRef.current,
    y: panRef.current.y + w.y * zoomRef.current,
  }), []);

  const ajustarVista = useCallback(() => {
    if (!bgRef.current || !canvasRef.current) return;
    const z = Math.min(VIEW_W / bgRef.current.width, VIEW_H / bgRef.current.height, 1);
    const p = { x: (VIEW_W - bgRef.current.width * z) / 2, y: (VIEW_H - bgRef.current.height * z) / 2 };
    zoomRef.current = z; panRef.current = p;
    setZoom(z); setPan(p);
  }, []);

  // Snapshot del propio fondo ya renderizado (JPEG, calidad reducida) para
  // poder guardarlo directamente en Firestore al pulsar "Guardar proyecto"
  // — sin esto habría que re-subir el archivo original a Storage cada vez.
  useImperativeHandle(ref, () => ({
    getFondoSnapshot: () => (bgRef.current ? bgRef.current.toDataURL('image/jpeg', 0.82) : null),
  }), []);

  useEffect(() => {
    if (!file && !bgSnapshotUrl) return;
    let cancelled = false;
    setLoadingPdf(true);
    setError('');
    (async () => {
      try {
        const off = document.createElement('canvas');
        const maxWidth = 1000;

        if (!file && bgSnapshotUrl) {
          const img = await new Promise((resolve, reject) => {
            const image = new window.Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('No se pudo cargar el plano guardado'));
            image.src = bgSnapshotUrl;
          });
          const scale = Math.min(1, maxWidth / img.width);
          off.width = img.width * scale;
          off.height = img.height * scale;
          off.getContext('2d').drawImage(img, 0, 0, off.width, off.height);
        } else if (file.type === 'application/pdf') {
          const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
          pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
          const arrayBuffer = await file.arrayBuffer();
          const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
          const page = await doc.getPage(1);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = maxWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });
          off.width = viewport.width;
          off.height = viewport.height;
          await page.render({ canvasContext: off.getContext('2d'), viewport }).promise;
        } else {
          const url = URL.createObjectURL(file);
          const img = await new Promise((resolve, reject) => {
            const image = new window.Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Formato de imagen no soportado'));
            image.src = url;
          });
          URL.revokeObjectURL(url);
          const scale = maxWidth / img.width;
          off.width = img.width * scale;
          off.height = img.height * scale;
          off.getContext('2d').drawImage(img, 0, 0, off.width, off.height);
        }

        if (cancelled) return;
        bgRef.current = off;
        ajustarVista();
      } catch (e) {
        if (!cancelled) setError('No se pudo leer el plano: ' + e.message);
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    })();
    return () => { cancelled = true; };
  }, [file, bgSnapshotUrl, ajustarVista]);

  // ── Dibujo ───────────────────────────────────────────────────────────────
  // Dos pasadas: la geometría (líneas/polígonos/equipo) dentro de la
  // transformación de zoom/pan, para que escale de forma natural; el texto
  // (etiquetas, círculos numerados) fuera, con la posición ya convertida a
  // pantalla a mano, para que se mantenga legible a cualquier zoom.
  const draw = useCallback((cursorWorld) => {
    const canvas = canvasRef.current;
    if (!canvas || !bgRef.current) return;
    const ctx = canvas.getContext('2d');
    canvas.width = VIEW_W; canvas.height = VIEW_H;
    ctx.clearRect(0, 0, VIEW_W, VIEW_H);

    const z = zoomRef.current;
    const equipoActual = dragRef.current?.tipo === 'equipo' && dragRef.current.livePos ? dragRef.current.livePos : equipoPos;
    const { w: equipoWmundo, h: equipoHmundo } = tamanoEquipoMundo(equipoPos, mmPorPx);

    // Mientras se arrastra un vértice (ver dragRef tipo 'vertice'), el tramo
    // o estancia afectado se dibuja con ese punto ya en su posición en vivo
    // — se sustituyen aquí, una vez, en vez de repetir la comprobación en
    // cada sitio que recorre trazadoPx/poligonoPx más abajo.
    const vertDrag = dragRef.current?.tipo === 'vertice' && dragRef.current.livePos ? dragRef.current : null;
    const tramosVista = vertDrag?.entidadTipo === 'tramo'
      ? tramos.map(t => (t.id === vertDrag.entidadId ? { ...t, trazadoPx: t.trazadoPx.map((p, i) => (i === vertDrag.indice ? vertDrag.livePos : p)) } : t))
      : tramos;
    const estanciasVista = vertDrag?.entidadTipo === 'estancia'
      ? estancias.map(e => (e.id === vertDrag.entidadId ? { ...e, poligonoPx: e.poligonoPx.map((p, i) => (i === vertDrag.indice ? vertDrag.livePos : p)) } : e))
      : estancias;

    ctx.save();
    ctx.translate(panRef.current.x, panRef.current.y);
    ctx.scale(z, z);
    ctx.drawImage(bgRef.current, 0, 0);

    // Cuadrícula de referencia — por debajo de todo lo demás, con un paso
    // en mm que se adapta al zoom actual para no verse ni saturada ni suelta.
    if (mostrarGrid && mmPorPx) {
      let pasoMm = PASOS_GRID_MM[PASOS_GRID_MM.length - 1];
      for (const p of PASOS_GRID_MM) {
        if ((p / mmPorPx) * z >= GRID_OBJETIVO_PX) { pasoMm = p; break; }
      }
      const pasoWorld = pasoMm / mmPorPx;
      const x0 = -panRef.current.x / z, y0 = -panRef.current.y / z;
      const x1 = (VIEW_W - panRef.current.x) / z, y1 = (VIEW_H - panRef.current.y) / z;
      ctx.strokeStyle = 'rgba(100,116,139,0.18)'; ctx.lineWidth = 1 / z;
      ctx.beginPath();
      for (let x = Math.floor(x0 / pasoWorld) * pasoWorld; x < x1; x += pasoWorld) { ctx.moveTo(x, y0); ctx.lineTo(x, y1); }
      for (let y = Math.floor(y0 / pasoWorld) * pasoWorld; y < y1; y += pasoWorld) { ctx.moveTo(x0, y); ctx.lineTo(x1, y); }
      ctx.stroke();
    }

    for (const e of estanciasVista) {
      if (!e.poligonoPx?.length) continue;
      const isSel = estaSeleccionado('estancia', e.id);
      ctx.beginPath();
      e.poligonoPx.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = 'rgba(34,197,94,0.18)';
      ctx.strokeStyle = isSel ? '#facc15' : '#16a34a';
      ctx.lineWidth = (isSel ? 3 : 2) / z;
      ctx.fill(); ctx.stroke();
    }

    // Tramos dibujados como tubo (borde oscuro + relleno claro, trazo
    // redondeado) en vez de una línea plana — se lee como un conducto real,
    // y los codos en escuadra quedan sin huecos gracias a lineJoin/lineCap.
    // El grosor refleja el ANCHO REAL de la sección ya calculada (en mm,
    // convertido a unidades "mundo" con mmPorPx) en vez de un grosor de
    // pantalla fijo — así un tronco de 700mm se ve claramente más grueso que
    // una derivación de 100mm, igual que se vería el conducto físico. Al no
    // dividir por z (a diferencia del resto del dibujo, en pantalla
    // constante), el trazo escala con el zoom exactamente igual que el
    // plano de fondo, porque ya está dentro de la transformación
    // ctx.scale(z,z). Antes de calcular (sin t.calculado todavía) se usa el
    // grosor de pantalla fijo de siempre, como valor de referencia.
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (const t of tramosVista) {
      if (!t.trazadoPx?.length) continue;
      const isSel = estaSeleccionado('tramo', t.id);
      const anchoSeccionMm = t.calculado?.seccion?.anchoMm;
      const grosorReal = anchoSeccionMm && mmPorPx ? anchoSeccionMm / mmPorPx : null;
      const minLegiblePx = (t.tipo === 'tronco' ? 3 : 2) / z;
      const grosorFallback = (t.tipo === 'tronco' ? 5 : 3.5) / z;
      const grosorBase = Math.max(grosorReal ?? grosorFallback, minLegiblePx);
      const trazar = () => {
        ctx.beginPath();
        t.trazadoPx.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      };
      trazar();
      ctx.strokeStyle = isSel ? '#eab308' : '#1e3a8a';
      ctx.lineWidth = grosorBase * 1.3;
      ctx.stroke();
      trazar();
      ctx.strokeStyle = isSel ? '#fde047' : t.tipo === 'tronco' ? '#60a5fa' : '#93c5fd';
      ctx.lineWidth = grosorBase;
      ctx.stroke();
    }

    // Embocadura: trapecio de transición donde un tramo arranca pegado al
    // equipo — no es una entidad guardada, se detecta por proximidad y se
    // dibuja siempre que haga falta, como haría el propio software de
    // diseño al detectar la conexión.
    if (equipoActual) {
      for (const t of tramosVista) {
        if (!t.trazadoPx?.length) continue;
        const inicio = t.trazadoPx[0];
        if (distanciaPx(inicio, equipoActual) > (equipoWmundo + 30)) continue;
        const dx = inicio.x - equipoActual.x, dy = inicio.y - equipoActual.y;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len, px = -uy, py = ux;
        const origen = bordeEquipoHaciaDireccion(equipoActual, ux, uy, equipoWmundo, equipoHmundo);
        const largo = 18, wEquipo = 17, wDucto = 9;
        const base1 = { x: origen.x + px * wEquipo, y: origen.y + py * wEquipo };
        const base2 = { x: origen.x - px * wEquipo, y: origen.y - py * wEquipo };
        const punta1 = { x: origen.x + ux * largo + px * wDucto, y: origen.y + uy * largo + py * wDucto };
        const punta2 = { x: origen.x + ux * largo - px * wDucto, y: origen.y + uy * largo - py * wDucto };
        ctx.beginPath();
        ctx.moveTo(base1.x, base1.y); ctx.lineTo(punta1.x, punta1.y);
        ctx.lineTo(punta2.x, punta2.y); ctx.lineTo(base2.x, base2.y);
        ctx.closePath();
        ctx.fillStyle = '#94a3b8'; ctx.strokeStyle = '#475569'; ctx.lineWidth = 1 / z;
        ctx.fill(); ctx.stroke();
      }
    }

    if (equipoActual) {
      const isSel = estaSeleccionado('equipo');
      ctx.save();
      ctx.translate(equipoActual.x, equipoActual.y);
      ctx.rotate(((equipoActual.rotacion || 0) * Math.PI) / 180);
      ctx.fillStyle = '#7c3aed';
      ctx.fillRect(-equipoWmundo / 2, -equipoHmundo / 2, equipoWmundo, equipoHmundo);
      ctx.strokeStyle = isSel ? '#facc15' : '#5b21b6';
      ctx.lineWidth = (isSel ? 4 : 2) / z;
      ctx.strokeRect(-equipoWmundo / 2, -equipoHmundo / 2, equipoWmundo, equipoHmundo);
      ctx.restore();
    }

    // Handles de edición: con exactamente un tramo o estancia seleccionado
    // (no tiene sentido con varios a la vez, ni mezclado con el equipo), se
    // marca cada vértice de su trazado con un círculo — arrastrable para
    // reformar la pieza sin tener que borrarla y volver a trazarla entera
    // (ver buscarVerticeCercano/dragRef tipo 'vertice').
    if (seleccionados.length === 1) {
      const sel = seleccionados[0];
      const puntosEntidad = sel.tipo === 'tramo'
        ? tramosVista.find(t => t.id === sel.id)?.trazadoPx
        : sel.tipo === 'estancia'
          ? estanciasVista.find(e => e.id === sel.id)?.poligonoPx
          : null;
      if (puntosEntidad) {
        const r = 5 / z;
        for (const p of puntosEntidad) {
          ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = '#fff'; ctx.fill();
          ctx.strokeStyle = '#eab308'; ctx.lineWidth = 2 / z; ctx.stroke();
        }
      }
    }

    // Rectángulo de selección por ventana, mientras se arrastra sobre vacío.
    if (dragRef.current?.tipo === 'select' && dragRef.current.moved) {
      const { startWorld, currentWorld } = dragRef.current;
      const x = Math.min(startWorld.x, currentWorld.x), y = Math.min(startWorld.y, currentWorld.y);
      const w = Math.abs(currentWorld.x - startWorld.x), h = Math.abs(currentWorld.y - startWorld.y);
      ctx.fillStyle = 'rgba(37,99,235,0.1)';
      ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 1 / z; ctx.setLineDash([4 / z, 3 / z]);
      ctx.fillRect(x, y, w, h); ctx.strokeRect(x, y, w, h); ctx.setLineDash([]);
    }

    if (puntos.length) {
      ctx.beginPath();
      puntos.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.strokeStyle = modo === 'estancia' ? '#16a34a' : modo === 'tramo' ? '#dc2626' : '#2563eb';
      ctx.lineWidth = 2 / z; ctx.setLineDash([5 / z, 3 / z]); ctx.stroke(); ctx.setLineDash([]);

      if (cursorWorld && (modo === 'estancia' || modo === 'tramo')) {
        const last = puntos[puntos.length - 1];
        ctx.beginPath();
        ctx.moveTo(last.x, last.y); ctx.lineTo(cursorWorld.x, cursorWorld.y);
        ctx.strokeStyle = '#94a3b8'; ctx.setLineDash([4 / z, 4 / z]); ctx.stroke(); ctx.setLineDash([]);
      }
      for (const p of puntos) {
        ctx.beginPath(); ctx.arc(p.x, p.y, 3.5 / z, 0, Math.PI * 2);
        ctx.fillStyle = '#2563eb'; ctx.fill();
      }
    }

    // Candidato de snap resaltado, mientras se coloca un punto — si es el
    // primer punto de un tramo nuevo y no hay un vértice cerca, se prueba a
    // enganchar en mitad del trazado de otro tramo (para poder arrancar una
    // derivación en cualquier punto de un tronco, no solo en sus extremos).
    if (cursorWorld && (modo === 'estancia' || modo === 'tramo')) {
      const candidatos = candidatosSnap(estancias, tramos, equipoPos);
      let snap = buscarSnap(cursorWorld, candidatos, z, SNAP_TOL_PX);
      if (!snap && modo === 'tramo' && puntos.length === 0) snap = buscarSnapEnTrazados(cursorWorld, tramos, z, SNAP_TOL_PX);
      if (snap) {
        ctx.beginPath(); ctx.arc(snap.x, snap.y, 6 / z, 0, Math.PI * 2);
        ctx.strokeStyle = '#facc15'; ctx.lineWidth = 2 / z; ctx.stroke();
      }
    }

    ctx.restore();

    // ── Anotaciones de texto, sin escalar con el zoom ──
    ctx.textAlign = 'center';
    for (const e of estanciasVista) {
      if (!e.poligonoPx?.length) continue;
      const c = worldToScreen(centroide(e.poligonoPx));
      ctx.fillStyle = '#166534'; ctx.font = 'bold 12px sans-serif';
      ctx.fillText(e.nombre || '', c.x, c.y - 6);
      if (e.area) { ctx.font = '10px sans-serif'; ctx.fillText(`${e.area} m²`, c.x, c.y + 8); }
    }

    // Conectores/collarines (cuadrado con cruz, ver dibujarConector) en cada
    // punto de unión T/Y — incluidos los que caen en mitad de un tronco, que
    // no coinciden con el final de ningún tramo — dibujados ANTES que los
    // círculos numerados de abajo, para que estos queden encima donde
    // coincidan (el final "Y" de un tramo padre) sin taparlos.
    for (const j of junctions) dibujarConector(ctx, worldToScreen(j.punto));

    tramosVista.forEach((t, idx) => {
      if (!t.trazadoPx?.length) return;
      if (t.calculado) {
        const midWorld = t.trazadoPx[Math.floor((t.trazadoPx.length - 1) / 2)];
        const mid = worldToScreen(midWorld);
        const texto = `${t.calculado.seccion.anchoMm}×${t.calculado.seccion.altoMm} · ${Math.round(t.calculado.caudalM3h)} m³/h`;
        ctx.font = 'bold 10px sans-serif';
        const w = ctx.measureText(texto).width;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(mid.x - w / 2 - 3, mid.y - 16, w + 6, 14);
        ctx.fillStyle = '#111827';
        ctx.fillText(texto, mid.x, mid.y - 6);
      }
      const finWorld = t.trazadoPx[t.trazadoPx.length - 1];
      const fin = worldToScreen(finWorld);
      // Conector también en el extremo final de cada tramo (llegada a una
      // estancia, o un empalme que no está en `junctions`) — así se lee como
      // un conducto real terminado en un collarín, no solo en los puntos de
      // ramificación.
      dibujarConector(ctx, fin);
      ctx.beginPath(); ctx.arc(fin.x, fin.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = t.tipo === 'tronco' ? '#1e3a8a' : '#3b82f6';
      ctx.fill(); ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.fillStyle = '#fff'; ctx.font = 'bold 9px sans-serif';
      ctx.fillText(String(idx + 1), fin.x, fin.y + 3);

      // Terminal (rejilla/difusor) — junto al círculo numerado, no encima,
      // para no tapar la numeración que ya se usa para correlacionar con
      // la tabla de tramos. Se edita con un desplegable en esa misma tabla,
      // no se selecciona ni se borra desde el propio canvas.
      if (t.terminal?.tipo) {
        dibujarTerminal(ctx, { x: fin.x, y: fin.y - 18 }, t.terminal.tipo, false);
      }

      // Accesorios en línea (compuerta/silenciador/filtro) del propio tramo.
      for (const a of t.accesorios || []) {
        dibujarAccesorio(ctx, worldToScreen(a.punto), a.tipo, estaSeleccionado('accesorio', a.id));
      }
    });

    if (equipoActual) {
      const c = worldToScreen(equipoActual);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 10px sans-serif';
      ctx.fillText('UI', c.x, c.y + 3);
    }

    if (cursorWorld && puntos.length && (modo === 'estancia' || modo === 'tramo') && mmPorPx) {
      const last = puntos[puntos.length - 1];
      const distM = longitudTrazadoM([last, cursorWorld], mmPorPx);
      const cs = worldToScreen(cursorWorld);
      ctx.font = 'bold 11px sans-serif'; ctx.fillStyle = '#1d4ed8';
      ctx.fillText(`${distM.toFixed(2)} m`, cs.x, cs.y - 16);
    }
    ctx.textAlign = 'left';
  // zoom/pan van también en las dependencias aunque draw() los lea de refs
  // (para poder llamarlo directo durante un arrastre, sin esperar al ciclo
  // de React) — si no, cambiar el zoom/pan por sí solo (rueda, "Ajustar
  // vista") no dispara el useEffect de más abajo y el <canvas> se queda con
  // el tamaño por defecto del navegador hasta el primer trazo.
  }, [estancias, tramos, puntos, modo, equipoPos, seleccionados, mmPorPx, worldToScreen, zoom, pan, junctions, mostrarGrid]);

  useEffect(() => { draw(mouseWorldRef.current); }, [draw]);

  const puntoDesdeEvento = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const screen = { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    return { screen, world: screenToWorld(screen) };
  };

  // ── Zoom (rueda, listener nativo para poder preventDefault) ────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      if (!bgRef.current) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width, scaleY = canvas.height / rect.height;
      const screen = { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
      const worldBefore = screenToWorld(screen);
      let nz = zoomRef.current * (e.deltaY < 0 ? 1.12 : 1 / 1.12);
      nz = Math.max(0.2, Math.min(5, nz));
      const newPan = { x: screen.x - worldBefore.x * nz, y: screen.y - worldBefore.y * nz };
      zoomRef.current = nz; panRef.current = newPan;
      setZoom(nz); setPan(newPan);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [screenToWorld, file]); // `file` es necesario: el <canvas> solo existe (return null si no hay file)
                              // cuando ya hay un plano cargado, así que el efecto tiene que re-engancharse
                              // en cuanto el canvas realmente se monta, no solo en el montaje inicial.

  // ── Selección (clic sin modo activo) ────────────────────────────────────────
  // Con Shift, añade/quita del conjunto seleccionado en vez de reemplazarlo
  // — mismo criterio que cualquier editor CAD (Shift+clic para multi-selección).
  const seleccionarEn = (world, shiftKey) => {
    const { w: eqW, h: eqH } = tamanoEquipoMundo(equipoPos, mmPorPx);
    let hit = null;
    // Los accesorios son objetivos pequeños — se comprueban primero, antes
    // que el equipo o el propio tramo al que pertenecen, para que un clic
    // cerca de uno no lo "tape" la selección del tramo entero.
    for (const t of tramos) {
      for (const a of t.accesorios || []) {
        if (distanciaPx(world, a.punto) < 10 / zoomRef.current) { hit = { tipo: 'accesorio', tramoId: t.id, id: a.id }; break; }
      }
      if (hit) break;
    }
    if (!hit && puntoEnEquipoRotado(world, equipoPos, eqW, eqH)) hit = { tipo: 'equipo' };
    if (!hit) for (const t of tramos) {
      if (t.trazadoPx?.length >= 2 && distanciaPuntoAPolilinea(world, t.trazadoPx) < 8 / zoomRef.current) { hit = { tipo: 'tramo', id: t.id }; break; }
    }
    if (!hit) for (const e of estancias) {
      if (e.poligonoPx?.length >= 3 && puntoEnPoligono(world, e.poligonoPx)) { hit = { tipo: 'estancia', id: e.id }; break; }
    }
    if (!hit) { if (!shiftKey) setSeleccionados([]); return; }
    if (shiftKey) {
      setSeleccionados(prev => estaSeleccionado(hit.tipo, hit.id)
        ? prev.filter(s => !(s.tipo === hit.tipo && (hit.tipo === 'equipo' || s.id === hit.id)))
        : [...prev, hit]);
    } else {
      setSeleccionados([hit]);
    }
  };

  // Vértice de la ÚNICA entidad seleccionada que caiga cerca de `world` —
  // solo tiene sentido comprobarlo con una sola cosa seleccionada (con
  // varias, o con el equipo, no hay vértices que editar de esta forma).
  const buscarVerticeCercano = (world) => {
    if (seleccionados.length !== 1) return null;
    const sel = seleccionados[0];
    const tolWorld = VERTICE_TOL_PX / zoomRef.current;
    if (sel.tipo === 'tramo') {
      const t = tramos.find(tt => tt.id === sel.id);
      if (!t?.trazadoPx) return null;
      for (let i = 0; i < t.trazadoPx.length; i++) if (distanciaPx(world, t.trazadoPx[i]) < tolWorld) return { entidadTipo: 'tramo', entidadId: t.id, indice: i };
    } else if (sel.tipo === 'estancia') {
      const e = estancias.find(ee => ee.id === sel.id);
      if (!e?.poligonoPx) return null;
      for (let i = 0; i < e.poligonoPx.length; i++) if (distanciaPx(world, e.poligonoPx[i]) < tolWorld) return { entidadTipo: 'estancia', entidadId: e.id, indice: i };
    }
    return null;
  };

  // ── Ratón: pan (botón derecho), arrastre del equipo/vértice, y selección
  // por ventana (arrastrar sobre vacío) con el botón izquierdo ────────────
  const handleMouseDown = (e) => {
    const { screen, world } = puntoDesdeEvento(e);
    if (e.button === 2) {
      dragRef.current = { tipo: 'pan', startScreen: screen, startPan: { ...panRef.current } };
      return;
    }
    if (e.button !== 0 || modo) return;
    const { w: eqW, h: eqH } = tamanoEquipoMundo(equipoPos, mmPorPx);
    const vert = buscarVerticeCercano(world);
    if (vert) {
      dragRef.current = { tipo: 'vertice', ...vert, startScreen: screen, moved: false, livePos: null };
      return;
    }
    if (puntoEnEquipoRotado(world, equipoPos, eqW, eqH)) {
      dragRef.current = { tipo: 'equipo', startScreen: screen, startEquipoPos: { ...equipoPos }, moved: false, livePos: null };
      return;
    }
    dragRef.current = { tipo: 'select', startScreen: screen, startWorld: world, currentWorld: world, moved: false, shift: e.shiftKey };
  };

  const handleMouseMove = (e) => {
    const { screen, world } = puntoDesdeEvento(e);
    mouseWorldRef.current = world;

    if (dragRef.current?.tipo === 'pan') {
      const dx = screen.x - dragRef.current.startScreen.x, dy = screen.y - dragRef.current.startScreen.y;
      panRef.current = { x: dragRef.current.startPan.x + dx, y: dragRef.current.startPan.y + dy };
      draw(null);
      return;
    }
    if (dragRef.current?.tipo === 'equipo') {
      const dx = screen.x - dragRef.current.startScreen.x, dy = screen.y - dragRef.current.startScreen.y;
      if (Math.hypot(dx, dy) > 4) dragRef.current.moved = true;
      dragRef.current.livePos = {
        ...dragRef.current.startEquipoPos,
        x: dragRef.current.startEquipoPos.x + dx / zoomRef.current,
        y: dragRef.current.startEquipoPos.y + dy / zoomRef.current,
      };
      draw(null);
      return;
    }
    if (dragRef.current?.tipo === 'vertice') {
      const dx = screen.x - dragRef.current.startScreen.x, dy = screen.y - dragRef.current.startScreen.y;
      if (Math.hypot(dx, dy) > 3) dragRef.current.moved = true;
      let target = world;
      const candidatos = candidatosSnap(estancias, tramos, equipoPos);
      const snap = buscarSnap(target, candidatos, zoomRef.current, SNAP_TOL_PX);
      if (snap) target = { x: snap.x, y: snap.y };
      dragRef.current.livePos = target;
      draw(null);
      return;
    }
    if (dragRef.current?.tipo === 'select') {
      const dx = screen.x - dragRef.current.startScreen.x, dy = screen.y - dragRef.current.startScreen.y;
      if (Math.hypot(dx, dy) > 4) dragRef.current.moved = true;
      dragRef.current.currentWorld = world;
      draw(null);
      return;
    }
    if (modo === 'estancia' || modo === 'tramo') draw(world);
  };

  const finalizarDrag = () => {
    // dragRef se limpia en cada rama antes de que el evento `click`
    // posterior llegue a handleClick — por eso se guarda si hubo
    // movimiento real en un ref aparte (justDraggedRef), para poder
    // distinguir en handleClick un arrastre real de un clic simple.
    if (dragRef.current?.tipo === 'pan') { setPan({ ...panRef.current }); dragRef.current = null; return; }
    if (dragRef.current?.tipo === 'equipo') {
      justDraggedRef.current = dragRef.current.moved;
      if (dragRef.current.moved && dragRef.current.livePos) onColocarEquipo(dragRef.current.livePos);
      dragRef.current = null;
      return;
    }
    if (dragRef.current?.tipo === 'vertice') {
      justDraggedRef.current = dragRef.current.moved;
      if (dragRef.current.moved && dragRef.current.livePos) {
        const { entidadTipo, entidadId, indice, livePos } = dragRef.current;
        if (entidadTipo === 'tramo') onEditarPuntoTramo?.(entidadId, indice, livePos);
        else if (entidadTipo === 'estancia') onEditarPuntoEstancia?.(entidadId, indice, livePos);
      }
      dragRef.current = null;
      return;
    }
    if (dragRef.current?.tipo === 'select') {
      if (dragRef.current.moved) {
        justDraggedRef.current = true;
        const { startWorld, currentWorld, shift } = dragRef.current;
        const xMin = Math.min(startWorld.x, currentWorld.x), xMax = Math.max(startWorld.x, currentWorld.x);
        const yMin = Math.min(startWorld.y, currentWorld.y), yMax = Math.max(startWorld.y, currentWorld.y);
        const dentro = (p) => p.x >= xMin && p.x <= xMax && p.y >= yMin && p.y <= yMax;
        const nuevos = [];
        for (const es of estancias) if (es.poligonoPx?.length && es.poligonoPx.every(dentro)) nuevos.push({ tipo: 'estancia', id: es.id });
        for (const t of tramos) if (t.trazadoPx?.length && t.trazadoPx.every(dentro)) nuevos.push({ tipo: 'tramo', id: t.id });
        if (equipoPos && dentro(equipoPos)) nuevos.push({ tipo: 'equipo' });
        setSeleccionados(prev => (shift ? [...prev, ...nuevos.filter(n => !prev.some(p => p.tipo === n.tipo && (n.tipo === 'equipo' || p.id === n.id)))] : nuevos));
      }
      dragRef.current = null;
      return;
    }
  };

  const handleClick = (e) => {
    if (justDraggedRef.current) { justDraggedRef.current = false; return; } // fue un arrastre, no un clic de selección
    const { world } = puntoDesdeEvento(e);

    if (modo === 'calibrar') {
      const nuevos = [...puntos, world];
      if (nuevos.length === 2) { setCalibPendiente(nuevos); setPuntos([]); }
      else setPuntos(nuevos);
      return;
    }
    if (modo === 'equipo') { onColocarEquipo(world); setModo(null); return; }
    if (modo === 'accesorio') {
      // Tolerancia más generosa que el snap habitual — acertar justo encima
      // de la línea de un tramo a ojo es más difícil que acertar un vértice.
      const hit = buscarTramoCercano(world, tramos, zoomRef.current, SNAP_TOL_PX * 2);
      if (hit) onNuevoAccesorio?.(hit.tramoId, hit.punto, tipoAccesorio);
      return;
    }
    if (modo === 'estancia' || modo === 'tramo') {
      let target = world;
      const candidatos = candidatosSnap(estancias, tramos, equipoPos);
      const snap = buscarSnap(target, candidatos, zoomRef.current, SNAP_TOL_PX);
      if (snap) target = { x: snap.x, y: snap.y };
      // Si es el primer punto de un tramo nuevo y no hay un vértice cerca,
      // se prueba a enganchar en mitad del trazado de otro tramo — así una
      // derivación puede arrancar en cualquier punto de un tronco, no solo
      // en sus extremos, tal como se diseña una instalación real.
      else if (modo === 'tramo' && puntos.length === 0) {
        const snapTrazado = buscarSnapEnTrazados(target, tramos, zoomRef.current, SNAP_TOL_PX);
        if (snapTrazado) target = { x: snapTrazado.x, y: snapTrazado.y };
      }
      else if (modo === 'tramo' && puntos.length > 0) target = snapOrtogonal(puntos[puntos.length - 1], target, ORTHO_SNAP_DEG);
      setPuntos(p => [...p, target]);
      return;
    }
    seleccionarEn(world, e.shiftKey);
  };

  const cancelarForma = () => { setPuntos([]); setModo(null); };

  const finalizarForma = () => {
    if (modo === 'estancia' && puntos.length >= 3) {
      const areaM2 = areaPoligonoM2(puntos, mmPorPx);
      onNuevaEstancia({ poligonoPx: puntos, areaM2 });
    } else if (modo === 'tramo' && puntos.length >= 2) {
      const longitudM = longitudTrazadoM(puntos, mmPorPx);
      onNuevoTramo({ trazadoPx: puntos, longitudM });
    }
    setPuntos([]); setModo(null);
  };

  const confirmarCalibracion = () => {
    const metros = Number(distanciaInput);
    if (!metros || !calibPendiente) return;
    onCalibrar(calcularMmPorPx(calibPendiente[0], calibPendiente[1], metros));
    setCalibPendiente(null); setDistanciaInput(''); setModo(null);
  };

  // Borra todo lo que haya en `seleccionados` de golpe — mismo camino tanto
  // desde el botón "Eliminar" como desde la tecla Supr (ver el useEffect
  // de atajos de teclado más abajo).
  const handleEliminarSeleccionado = () => {
    if (!seleccionados.length) return;
    for (const s of seleccionados) {
      if (s.tipo === 'equipo') onColocarEquipo(null);
      else if (s.tipo === 'estancia') onEliminarEstancia?.(s.id);
      else if (s.tipo === 'tramo') onEliminarTramo?.(s.id);
      else if (s.tipo === 'accesorio') onEliminarAccesorio?.(s.tramoId, s.id);
    }
    setSeleccionados([]);
  };

  // Atajos: Supr/Backspace borra la selección actual, Escape la limpia (o
  // cancela la forma que se esté trazando, si hay una en curso). Se ignora
  // si el foco está en un campo de texto (p. ej. el de calibración).
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && seleccionados.length) {
        e.preventDefault();
        handleEliminarSeleccionado();
      } else if (e.key === 'Escape') {
        if (modo) { setPuntos([]); setModo(null); } else setSeleccionados([]);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [seleccionados, modo]);

  const nombreSeleccionado = () => {
    if (seleccionados.length === 0) return '';
    if (seleccionados.length > 1) return `${seleccionados.length} elementos`;
    const sel = seleccionados[0];
    if (sel.tipo === 'equipo') return 'Equipo';
    if (sel.tipo === 'estancia') return estancias.find(e => e.id === sel.id)?.nombre || 'Estancia';
    if (sel.tipo === 'accesorio') {
      const t = tramos.find(tt => tt.id === sel.tramoId);
      const a = t?.accesorios?.find(aa => aa.id === sel.id);
      const label = TIPOS_ACCESORIO.find(ta => ta.id === a?.tipo)?.label || 'Accesorio';
      return `${label} (${t?.nombre || 'tramo'})`;
    }
    return tramos.find(t => t.id === sel.id)?.nombre || 'Tramo';
  };

  // Desglose del cálculo de un tramo, para que se vea de dónde sale la
  // sección — la velocidad no viaja en t.calculado, así que se despeja de
  // los propios números ya calculados (caudal = 3600 × velocidad × sección).
  const desgloseTramo = () => {
    if (seleccionados.length !== 1 || seleccionados[0].tipo !== 'tramo') return null;
    const t = tramos.find(tr => tr.id === seleccionados[0].id);
    if (!t?.calculado) return null;
    const { caudalM3h, seccion } = t.calculado;
    const velocidad = seccion.seccionM2 > 0 ? caudalM3h / (3600 * seccion.seccionM2) : 0;
    return `Caudal ${Math.round(caudalM3h)} m³/h ÷ (3600 × ${velocidad.toFixed(1)} m/s) = ${seccion.seccionM2.toFixed(3)} m² → ${seccion.anchoMm}×${seccion.altoMm} mm`;
  };

  if (!file && !bgSnapshotUrl) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {MODOS.map(m => {
          const activo = modo === m.id;
          return (
            <button key={m.id} onClick={() => { setModo(activo ? null : m.id); setPuntos([]); setSeleccionados([]); }}
              className="px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors"
              style={activo ? { backgroundColor: m.color, borderColor: m.color, color: '#fff' } : { backgroundColor: '#fff', borderColor: '#e5e7eb', color: '#4b5563' }}>
              {m.label}
            </button>
          );
        })}
        <button onClick={ajustarVista} className="px-3 py-1.5 text-xs font-semibold rounded-full border border-gray-200 text-gray-600 bg-white">Ajustar vista</button>
        <button onClick={() => setMostrarGrid(v => !v)}
          className="px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors"
          style={mostrarGrid ? { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1', color: '#334155' } : { backgroundColor: '#fff', borderColor: '#e5e7eb', color: '#9ca3af' }}>
          # Cuadrícula
        </button>
        {mmPorPx ? (
          <span className="text-xs text-gray-400">Escala: 1px = {mmPorPx.toFixed(1)}mm · zoom {(zoom * 100).toFixed(0)}%</span>
        ) : (
          <span className="text-xs text-amber-600">Calibra la escala antes de trazar estancias o tramos</span>
        )}
      </div>
      <p className="text-[11px] text-gray-400">Rueda del ratón: zoom · botón derecho + arrastrar: mover la vista · sin modo activo: clic para seleccionar (Shift+clic para varios, arrastra sobre vacío para seleccionar por ventana) · Supr para borrar · Ctrl+Z para deshacer.</p>

      {(modo === 'estancia' || modo === 'tramo') && (
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600">
          Haz clic sobre el plano para ir marcando {modo === 'estancia' ? 'el contorno de la estancia' : 'la ruta del tramo'} ({puntos.length} puntos).
          {modo === 'tramo' && puntos.length === 0 && (
            <span className="text-gray-400"> El primer clic puede caer sobre otro tramo ya trazado, para arrancar una derivación en mitad de un tronco.</span>
          )}
          <button onClick={finalizarForma} disabled={puntos.length < (modo === 'estancia' ? 3 : 2)}
            className="ml-auto px-2.5 py-1 bg-gray-800 disabled:opacity-30 text-white rounded-lg font-semibold">Finalizar</button>
          <button onClick={cancelarForma} className="px-2.5 py-1 text-gray-400 hover:text-red-500">Cancelar</button>
        </div>
      )}

      {modo === 'equipo' && (
        <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600">
          Haz clic sobre el plano en el punto donde va el equipo{equipoPos ? ' (esto sustituirá la posición actual)' : ''}.
        </div>
      )}

      {modo === 'accesorio' && (
        <div className="flex flex-wrap items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600">
          <span>Tipo:</span>
          {TIPOS_ACCESORIO.map(ta => (
            <button key={ta.id} onClick={() => setTipoAccesorio(ta.id)}
              className={`px-2.5 py-1 rounded-full border text-xs font-semibold ${tipoAccesorio === ta.id ? 'bg-gray-700 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-500'}`}>
              {ta.label}
            </button>
          ))}
          <span className="text-gray-400">Haz clic sobre un tramo ya trazado para colocarlo ahí.</span>
        </div>
      )}

      {calibPendiente && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs">
          <span className="text-blue-700">Distancia real entre esos dos puntos (metros):</span>
          <input type="number" step="0.01" autoFocus value={distanciaInput} onChange={e => setDistanciaInput(e.target.value)}
            className="w-20 border border-gray-200 rounded px-2 py-1" onKeyDown={e => e.key === 'Enter' && confirmarCalibracion()} />
          <button onClick={confirmarCalibracion} className="px-2.5 py-1 bg-blue-600 text-white rounded-lg font-semibold">Calibrar</button>
          <button onClick={() => { setCalibPendiente(null); setModo(null); }} className="text-gray-400 hover:text-red-500">Cancelar</button>
        </div>
      )}

      {seleccionados.length > 0 && !modo && (
        <div className="flex flex-wrap items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-xs">
          <span className="text-yellow-800">Seleccionado: {nombreSeleccionado()}</span>
          {desgloseTramo() && <span className="text-gray-500 basis-full">{desgloseTramo()}</span>}
          {seleccionados.length === 1 && seleccionados[0].tipo === 'equipo' && (
            <>
              {onRotarEquipo && <button onClick={onRotarEquipo} className="px-2.5 py-1 bg-purple-600 text-white rounded-lg font-semibold">⟳ Girar 90°</button>}
              {onTamanoEquipo && (
                <span className="flex items-center gap-1 text-gray-500">
                  Tamaño real (mm):
                  <input type="number" className="w-16 border border-gray-200 rounded px-1.5 py-1"
                    value={equipoPos?.anchoMm || EQUIPO_ANCHO_MM_DEFECTO}
                    onChange={ev => onTamanoEquipo('anchoMm', Number(ev.target.value) || EQUIPO_ANCHO_MM_DEFECTO)} />
                  ×
                  <input type="number" className="w-16 border border-gray-200 rounded px-1.5 py-1"
                    value={equipoPos?.altoMm || EQUIPO_ALTO_MM_DEFECTO}
                    onChange={ev => onTamanoEquipo('altoMm', Number(ev.target.value) || EQUIPO_ALTO_MM_DEFECTO)} />
                </span>
              )}
            </>
          )}
          {seleccionados.length === 1 && (seleccionados[0].tipo === 'tramo' || seleccionados[0].tipo === 'estancia') && (
            <span className="text-gray-400">Arrastra un vértice (círculo amarillo) para ajustar la forma.</span>
          )}
          <button onClick={handleEliminarSeleccionado} className="ml-auto px-2.5 py-1 bg-red-600 text-white rounded-lg font-semibold">Eliminar</button>
          <button onClick={() => setSeleccionados([])} className="px-2.5 py-1 text-gray-400 hover:text-gray-600">Cerrar</button>
        </div>
      )}

      {loadingPdf && <p className="text-sm text-gray-400">Cargando plano…</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="border border-gray-200 rounded-xl overflow-hidden" style={{ width: VIEW_W, maxWidth: '100%' }}>
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={finalizarDrag}
          onMouseLeave={finalizarDrag}
          onContextMenu={e => e.preventDefault()}
          style={{ cursor: modo ? 'crosshair' : 'default', width: '100%', height: 'auto', display: 'block' }}
        />
      </div>
    </div>
  );
});

export default PlanoTracer;
